import { SetStateAction, useEffect, useMemo, useState } from 'react';

import { DEFAULT_PAGE_SIZE } from 'consts';
import { useApiAccess } from 'contexts/AccessProvider';
import {
  useListGmpMessagesQuery,
  useListSubmitGatewayEventsTaskQuery,
  useListTasksQuery,
  useTelemetryQuery,
} from 'gql';
import { usePagination, useSortBy, useWindowSize } from 'hooks';
import useTelemetry from 'pages/Home/HomePage/components/Telemetry/useTelemetry';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnsType } from 'types';

import { GMPMessageColumn } from './components/GMPMessageColumns';
import { ReadTaskColumns } from './components/ReadTaskColumns';
import { SubmitTaskColumns } from './components/SubmitTaskColumn';
import { taskColumn } from './components/TaskColumns';

type GMPListProps = {
  section: SectionType;
  onSectionChange: (section: SectionType) => void;
};

export type TabType = 'gmpmessages' | 'readmessagetasks' | 'submitgatewaytasks';
const tabs = [
  { id: 'gmpmessages', label: 'GMP Messages' },
  { id: 'readmessagetasks', label: 'Read Message Tasks' },
  { id: 'submitgatewaytasks', label: 'Submit Gateway Tasks' },
];

export type SectionType = 'messages' | 'chronicles' | 'shards';
const sections = [
  { id: 'messages', label: 'GMP' },
  { id: 'chronicles', label: 'Chronicles' },
  { id: 'shards', label: 'Shards' },
];

function useGMPList({ section, onSectionChange }: GMPListProps) {
  const { sessionKey } = useApiAccess();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [phase, setPhase] = useState('');
  const [selectedTab, setSelectedTab] = useState<TabType>(() => {
    const tabFromUrl = searchParams.get('tab') as TabType;
    return tabFromUrl && tabs?.map((tab) => tab?.id).includes(tabFromUrl)
      ? tabFromUrl
      : 'gmpmessages';
  });
  const [previousTab, setPreviousTab] = useState<TabType>(selectedTab);
  const windowSize = useWindowSize();

  const {
    PageController: TaskPageController,
    setTotalDataCount: setTotalTaskDataCount,
    pageSize: taskPageSize,
    pageNumber: taskPageNumber,
    setPageNumber: setTaskPageNumber,
  } = usePagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    shouldUpdateUrl: true,
  });

  const {
    PageController: GMPMessagePageController,
    setTotalDataCount: setTotalGMPMessageDataCount,
    pageSize: GMPMessagePageSize,
    pageNumber: GMPMessagePageNumber,
    setPageNumber: setGMPMessagePageNumber,
  } = usePagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    shouldUpdateUrl: true,
  });

  const {
    PageController: SubmitGatewayEventPageController,
    setTotalDataCount: setTotalSubmitGatewayEventDataCount,
    pageSize: submitGatewayEventPageSize,
    pageNumber: submitGatewayEventPageNumber,
    setPageNumber: setSubmitGatewayEventPageNumber,
  } = usePagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    shouldUpdateUrl: true,
  });

  useEffect(() => {
    // Only set tab parameter if we're in the messages section
    if (section === 'messages') {
      setSearchParams((prev) => {
        prev.set('tab', selectedTab);
        return prev;
      });

      if (selectedTab !== previousTab) {
        if (selectedTab === 'gmpmessages') {
          setGMPMessagePageNumber(1);
        } else if (selectedTab === 'readmessagetasks') {
          setTaskPageNumber(1);
        } else if (selectedTab === 'submitgatewaytasks') {
          setSubmitGatewayEventPageNumber(1);
        }
        setPreviousTab(selectedTab);
      }
    } else {
      setSearchParams((prev) => {
        prev.delete('tab');
        return prev;
      });
    }
  }, [
    selectedTab,
    previousTab,
    setSearchParams,
    setGMPMessagePageNumber,
    setTaskPageNumber,
    setSubmitGatewayEventPageNumber,
    section,
  ]);

  // const { FilterController, selectedField } = useFilter(FilterType.Tasks, setPageNumber);

  const { sortField, sortOrder, onSort } = useSortBy();

  const { data: GMPMessageData, loading: gmpMessageLoading } = useListGmpMessagesQuery({
    variables: {
      sessionKey,
      page: { size: GMPMessagePageSize, number: GMPMessagePageNumber },
      ...(sortField && {
        sort: { field: sortField, order: sortOrder },
      }),
    },
    // skip: selectedField.column ? !selectedField.value : false,
    onCompleted(data) {
      setTotalGMPMessageDataCount(data.listGMPMessages.count);
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: SubmitGatewayEventData, loading: submitGatewayEventLoading } =
    useListSubmitGatewayEventsTaskQuery({
      variables: {
        sessionKey,
        page: { size: submitGatewayEventPageSize, number: submitGatewayEventPageNumber },
        ...(sortField && {
          sort: { field: sortField, order: sortOrder },
        }),
      },
      // skip: selectedField.column ? !selectedField.value : false,
      onCompleted(data) {
        setTotalSubmitGatewayEventDataCount(data.listSubmitGatewayEventsTask.count);
      },
      fetchPolicy: 'cache-and-network',
    });

  const {
    data: taskData,
    loading: tasksLoading,
    previousData,
  } = useListTasksQuery({
    variables: {
      sessionKey,
      page: { size: taskPageSize, number: taskPageNumber },
      ...(sortField && {
        sort: { field: sortField, order: sortOrder },
      }),
    },
    // skip: selectedField.column ? !selectedField.value : false,
    onCompleted(data) {
      setTotalTaskDataCount(data.listTasks.count);
    },
    fetchPolicy: 'cache-and-network',
  });

  const { tasksData, taskColumns } = useMemo(() => {
    const array = taskColumn({ navigate, setPhase });
    if (windowSize.width < 769 && windowSize.width !== 0) {
      const element = array.splice(6, 1)[0];
      array.splice(3, 0, element);
    }
    return {
      tasksData: taskData ? taskData : previousData,
      taskColumns: array,
    };
  }, [navigate, taskData]);

  const [pageSize, PageController] = useMemo(
    () => [taskPageSize, TaskPageController],
    [taskPageSize, TaskPageController]
  );

  const tableData: Record<
    TabType,
    {
      columns: ColumnsType[];
      data: any;
      isLoading: boolean;
      count: number | undefined;
      PageController: JSX.Element;
      pageSize: number;
    }
  > = {
    gmpmessages: {
      columns: GMPMessageColumn(),
      data: GMPMessageData?.listGMPMessages,
      isLoading: gmpMessageLoading,
      count: GMPMessageData?.listGMPMessages?.count,
      PageController: <GMPMessagePageController />,
      pageSize: GMPMessagePageSize,
    },
    readmessagetasks: {
      columns: ReadTaskColumns({ navigate }),
      data: taskData?.listTasks,
      isLoading: tasksLoading,
      count: taskData?.listTasks.count,
      PageController: <TaskPageController />,
      pageSize: taskPageSize,
    },
    submitgatewaytasks: {
      columns: SubmitTaskColumns({ navigate }),
      data: SubmitGatewayEventData?.listSubmitGatewayEventsTask,
      isLoading: submitGatewayEventLoading,
      count: SubmitGatewayEventData?.listSubmitGatewayEventsTask?.count,
      PageController: <SubmitGatewayEventPageController />,
      pageSize: submitGatewayEventPageSize,
    },
  };

  const { data: telemetryData } = useTelemetryQuery({
    variables: { sessionKey },
    fetchPolicy: 'cache-and-network',
  });

  const { gmpStats } = useTelemetry({
    telemetryData,
    tokenDistributionData: undefined,
  });

  const handleTabChange = (value: SetStateAction<string>) => {
    const label = typeof value === 'function' ? value('') : value;
    const tab = tabs.find((t) => t.label === label);
    if (tab) {
      setSelectedTab(tab.id as TabType);
    }
  };

  const handleSectionTabChange = (tab: React.SetStateAction<string>) => {
    const tabValue = typeof tab === 'function' ? tab('') : tab;
    const newSection = sections.find((s) => s.label === tabValue)?.id;
    if (newSection) {
      onSectionChange(newSection as 'messages' | 'chronicles' | 'shards');
      setSearchParams(() => {
        const newParams = new URLSearchParams();
        if (newSection !== 'messages') {
          newParams.set('section', newSection);
        }
        return newParams;
      });
    }
  };

  return {
    tasksDataCount: taskData ? taskData?.listTasks.count : previousData?.listTasks.data,
    tasksLoading,
    sortOrder,
    onSort,
    taskColumns,
    tasksData,
    pageSize,
    PageController,
    // FilterController,
    selectedTab,
    setSelectedTab,
    tabs,
    tableData,
    gmpStats,
    sections,
    handleTabChange,
    handleSectionTabChange,
  };
}

export default useGMPList;
